import { secureGetItem, secureSetItem, secureRemoveItem } from './secureStorage';

interface TokenData {
  id: number;
  username: string;
  role: string;
  exp: number;
  iat: number;
}

export class TokenManager {
  private static instance: TokenManager;
  private refreshTimer: NodeJS.Timeout | null = null;
  private readonly REFRESH_THRESHOLD = 30 * 60 * 1000; // 30 dakika önceden yenile

  private constructor() {}

  public static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  /**
   * Token'ı parse eder ve bilgilerini döndürür
   */
  public parseToken(token: string): TokenData | null {
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      return decoded as TokenData;
    } catch (error) {
      // Token parse hatası
      return null;
    }
  }

  /**
   * Token'ın süresi dolmuş mu kontrol eder
   */
  public isTokenExpired(token: string): boolean {
    const tokenData = this.parseToken(token);
    if (!tokenData) return true;

    const currentTime = Date.now() / 1000;
    return tokenData.exp < currentTime;
  }

  /**
   * Token'ın yakında süresi dolacak mı kontrol eder
   */
  public isTokenExpiringSoon(token: string): boolean {
    const tokenData = this.parseToken(token);
    if (!tokenData) return true;

    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = (tokenData.exp - currentTime) * 1000; // milliseconds
    
    return timeUntilExpiry < this.REFRESH_THRESHOLD;
  }

  /**
   * Token'ı yeniler
   */
  public async refreshToken(): Promise<boolean> {
    try {
      const token = secureGetItem('token');
      if (!token) return false;

      const response = await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/users/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        secureSetItem('token', data.token);
        // Token başarıyla yenilendi
        return true;
      } else {
        // Token yenileme başarısız
        return false;
      }
    } catch (error) {
      // Token yenileme hatası
      return false;
    }
  }

  /**
   * Otomatik token yenileme sistemini başlatır
   */
  public startAutoRefresh(): void {
    this.stopAutoRefresh(); // Önceki timer'ı temizle

    const token = secureGetItem('token');
    if (!token) return;

    const tokenData = this.parseToken(token);
    if (!tokenData) return;

    // Token'ın ne zaman yenilenmesi gerektiğini hesapla
    const currentTime = Date.now() / 1000;
    const timeUntilRefresh = (tokenData.exp - currentTime) * 1000 - this.REFRESH_THRESHOLD;

    // Eğer token zaten yenilenmesi gerekiyorsa, hemen yenile
    if (timeUntilRefresh <= 0) {
      this.refreshToken().then(success => {
        if (success) {
          this.startAutoRefresh();
        } else {
          this.handleTokenRefreshFailure();
        }
      });
      return;
    }

    this.refreshTimer = setTimeout(async () => {
      const success = await this.refreshToken();
      if (success) {
        // Yenilenen token için yeni timer başlat
        this.startAutoRefresh();
      } else {
        // Token yenilenemedi, kullanıcıyı logout et
        this.handleTokenRefreshFailure();
      }
    }, timeUntilRefresh);

    // Token otomatik yenileme başlatılıyor
  }

  /**
   * Otomatik token yenileme sistemini durdurur
   */
  public stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Token yenileme başarısız olduğunda çağrılır
   */
  private handleTokenRefreshFailure(): void {
    // Token yenileme başarısız, kullanıcı logout ediliyor
    secureRemoveItem('token');
    secureRemoveItem('hasCheckedUpdates');
    
    // Login sayfasına yönlendir
    window.location.href = '/login';
  }

  /**
   * Token'ı kontrol eder ve gerekirse yeniler
   */
  public async checkAndRefreshToken(): Promise<boolean> {
    const token = secureGetItem('token');
    if (!token) return false;

    if (this.isTokenExpired(token)) {
      // Token süresi dolmuş
      return false;
    }

    if (this.isTokenExpiringSoon(token)) {
      // Token yakında süresi dolacak, yenileniyor
      return await this.refreshToken();
    }

    return true;
  }

  /**
   * Token bilgilerini döndürür
   */
  public getTokenInfo(token: string): { isValid: boolean; expiresAt: Date | null; timeUntilExpiry: number | null } {
    const tokenData = this.parseToken(token);
    
    if (!tokenData) {
      return { isValid: false, expiresAt: null, timeUntilExpiry: null };
    }

    const expiresAt = new Date(tokenData.exp * 1000);
    const timeUntilExpiry = tokenData.exp - (Date.now() / 1000);

    return {
      isValid: !this.isTokenExpired(token),
      expiresAt,
      timeUntilExpiry: timeUntilExpiry > 0 ? timeUntilExpiry : 0
    };
  }
}

// Singleton instance
export const tokenManager = TokenManager.getInstance();

